/**
 * Contact Map Block Script
 * Handles region selection and dynamic contact display
 */

const ACTIVE_REGION_COLOR = '#0D237D';
const DEFAULT_REGION_COLOR = '#C5CAE9';
const ACTIVE_TEXT_COLOR = '#FFFFFF';
const DEFAULT_TEXT_COLOR = '#000215';

document.addEventListener('DOMContentLoaded', function() {
    const blocks = document.querySelectorAll('[data-block-type="contact-map"]');
    
    blocks.forEach(block => {
        initContactMapBlock(block);
    });
});

/**
 * Initialize a single contact map block
 * @param {HTMLElement} block - The block container element
 */
function initContactMapBlock(block) {
    const selector = block.querySelector('select');
    const contactLists = block.querySelectorAll('[data-region-index]');
    const svg = block.querySelector('svg');
    
    if (!selector || !contactLists.length) {
        console.warn('Contact map: selector or contact lists not found', { selector, contactLists: contactLists.length });
        return;
    }
    
    // Store original colors from SVG paths
    if (svg) {
        storeOriginalColors(svg);
        initSvgInteractions(svg, selector);
    }
    
    // Highlight initial selected region
    const initialSelectedOption = selector.options[selector.selectedIndex];
    if (initialSelectedOption && svg) {
        const regionId = initialSelectedOption.dataset.regionId;
        highlightRegion(svg, regionId);
        // Store currently selected region ID on SVG
        svg.setAttribute('data-selected-region', regionId);
    }
    
    // Handle region selection change
    selector.addEventListener('change', function(e) {
        const selectedOption = selector.options[selector.selectedIndex];
        const regionId = selectedOption ? selectedOption.dataset.regionId : null;
        if (svg && regionId) {
            svg.setAttribute('data-selected-region', regionId);
        }
        handleRegionChange(e, contactLists, selector, svg);
    });
    
    // Initialize keyboard navigation
    initKeyboardNavigation(selector, contactLists);
}

/**
 * Handle region selection change
 * @param {Event} e - The change event
 * @param {NodeList} contactLists - All contact list elements
 * @param {HTMLElement} selector - The select element
 * @param {SVGElement} svg - The SVG map element
 */
function handleRegionChange(e, contactLists, selector, svg) {
    const selectedIndex = parseInt(e.target.value, 10);
    const selectedOption = selector.options[selector.selectedIndex];
    const regionId = selectedOption ? selectedOption.dataset.regionId : null;
    
    // Hide all contact lists
    contactLists.forEach(list => {
        list.classList.add('hidden');
        list.setAttribute('aria-hidden', 'true');
    });
    
    // Show selected contact list
    const selectedList = Array.from(contactLists).find(
        list => parseInt(list.dataset.regionIndex, 10) === selectedIndex
    );
    
    if (selectedList) {
        selectedList.classList.remove('hidden');
        selectedList.setAttribute('aria-hidden', 'false');
        
        // Announce change to screen readers using template from data attribute
        const regionName = selectedOption.text;
        const block = selector.closest('[data-block-type="contact-map"]');
        const template = block ? block.dataset.srRegionChange : 'Wyświetlanie kontaktów dla regionu {region}';
        const announcement = template.replace('{region}', regionName);
        announceToScreenReader(announcement);
    }
    
    // Highlight region on map
    if (svg && regionId) {
        highlightRegion(svg, regionId);
    }
}

/**
 * Initialize keyboard navigation enhancements
 * @param {HTMLElement} selector - The select element
 * @param {NodeList} contactLists - All contact list elements
 */
function initKeyboardNavigation(selector, contactLists) {
    // Native select handles all keyboard interactions
    // No additional handlers needed
}

/**
 * Focus the first contact card in the visible list
 * @param {HTMLElement} list - The visible contact list
 */
function focusFirstContact(list) {
    const firstCard = list.querySelector('article');
    if (firstCard) {
        // Set temporary tabindex for focus
        const firstLink = firstCard.querySelector('a');
        if (firstLink) {
            setTimeout(() => {
                firstLink.focus({ preventScroll: false });
            }, 100);
        }
    }
}

/**
 * Announce a message to screen readers
 * @param {string} message - The message to announce
 */
function announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0;';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
        document.body.removeChild(announcement);
    }, 1000);
}

/**
 * Store original colors from SVG paths
 * @param {SVGElement} svg - The SVG map element
 */
function storeOriginalColors(svg) {
    const allRegionGroups = svg.querySelectorAll('g[id]');
    allRegionGroups.forEach(group => {
        const paths = group.querySelectorAll('path');
        paths.forEach(path => {
            const fillColor = path.getAttribute('fill');
            if (fillColor && fillColor !== 'none' && !path.hasAttribute('data-original-fill')) {
                path.setAttribute('data-original-fill', fillColor);
            }
        });
        
        const regionTexts = group.querySelectorAll('.region');
        regionTexts.forEach(text => {
            const fillColor = text.getAttribute('fill');
            if (fillColor && !text.hasAttribute('data-original-fill')) {
                text.setAttribute('data-original-fill', fillColor);
            }
        });
    });
}

/**
 * Initialize SVG interactions (click and hover)
 * @param {SVGElement} svg - The SVG map element
 * @param {HTMLSelectElement} selector - The select element
 */
function initSvgInteractions(svg, selector) {
    const allRegionGroups = svg.querySelectorAll('g[id]');
    
    allRegionGroups.forEach(group => {
        const regionId = group.getAttribute('id');
        if (!regionId) return;
        
        // Make group interactive (mouse only)
        group.style.cursor = 'pointer';
        
        // Click handler
        group.addEventListener('click', function(e) {
            e.preventDefault();
            selectRegionById(regionId, selector);
        });
        
        // Hover handlers for groups with region-full class
        if (group.classList.contains('region-full')) {
            group.addEventListener('mouseenter', function() {
                const selectedRegionId = svg.getAttribute('data-selected-region');
                // Don't apply hover to currently selected region
                if (regionId === selectedRegionId) return;
                
                // Apply hover color to all paths inside
                const paths = group.querySelectorAll('path');
                paths.forEach(path => {
                    if (path.hasAttribute('fill') && path.getAttribute('fill') !== 'none') {
                        path.setAttribute('fill', ACTIVE_REGION_COLOR);
                    }
                });
                
                // Apply hover color to all text elements with class 'region'
                const regionTexts = group.querySelectorAll('.region');
                regionTexts.forEach(text => {
                    text.setAttribute('fill', ACTIVE_TEXT_COLOR);
                });
            });
            
            group.addEventListener('mouseleave', function() {
                const selectedRegionId = svg.getAttribute('data-selected-region');
                // Don't reset currently selected region
                if (regionId === selectedRegionId) return;
                
                // Reset to original colors
                const paths = group.querySelectorAll('path');
                paths.forEach(path => {
                    const originalFill = path.getAttribute('data-original-fill');
                    if (originalFill && path.hasAttribute('fill') && path.getAttribute('fill') !== 'none') {
                        path.setAttribute('fill', originalFill);
                    }
                });
                
                // Reset text to original color
                const regionTexts = group.querySelectorAll('.region');
                regionTexts.forEach(text => {
                    const originalFill = text.getAttribute('data-original-fill');
                    if (originalFill) {
                        text.setAttribute('fill', originalFill);
                    }
                });
            });
        }
    });
}

/**
 * Select region by ID and update the selector
 * @param {string} regionId - The ID of the region
 * @param {HTMLSelectElement} selector - The select element
 */
function selectRegionById(regionId, selector) {
    // Find option with matching region ID
    const options = Array.from(selector.options);
    const matchingOption = options.find(option => option.dataset.regionId === regionId);
    
    if (matchingOption) {
        selector.value = matchingOption.value;
        // Trigger change event
        const event = new Event('change', { bubbles: true });
        selector.dispatchEvent(event);
    } else {
        console.warn(`Contact map: No select option found for region "${regionId}"`);
    }
}

/**
 * Highlight a region on the SVG map
 * @param {SVGElement} svg - The SVG map element
 * @param {string} regionId - The ID of the region to highlight
 */
function highlightRegion(svg, regionId) {
    if (!svg || !regionId) return;
    
    // Reset all regions to their original colors
    const allRegionGroups = svg.querySelectorAll('g[id]');
    allRegionGroups.forEach(group => {
        // Reset paths to original fill
        const paths = group.querySelectorAll('path');
        paths.forEach(path => {
            const originalFill = path.getAttribute('data-original-fill');
            if (originalFill && path.hasAttribute('fill') && path.getAttribute('fill') !== 'none') {
                path.setAttribute('fill', originalFill);
            }
        });
        
        // Reset text to original color
        const regionTexts = group.querySelectorAll('.region');
        regionTexts.forEach(text => {
            const originalFill = text.getAttribute('data-original-fill');
            if (originalFill) {
                text.setAttribute('fill', originalFill);
            }
        });
    });
    
    // Highlight selected region
    const targetGroup = svg.querySelector(`g#${regionId}`) ||
                       svg.querySelector(`g[id="${regionId}"]`) ||
                       svg.querySelector(`g[data-region="${regionId}"]`);
    
    if (targetGroup) {
        // Highlight region paths
        const paths = targetGroup.querySelectorAll('path');
        paths.forEach(path => {
            if (path.hasAttribute('fill') && path.getAttribute('fill') !== 'none') {
                path.setAttribute('fill', ACTIVE_REGION_COLOR);
            }
        });
        
        // Change text color to white for selected region
        const regionTexts = targetGroup.querySelectorAll('.region');
        regionTexts.forEach(text => {
            text.setAttribute('fill', ACTIVE_TEXT_COLOR);
        });
        
        console.log(`Contact map: highlighted region "${regionId}"`, { 
            paths: paths.length, 
            texts: regionTexts.length 
        });
    } else {
        console.warn(`Contact map: region group not found for ID "${regionId}"`);
    }
}
